<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { DxForm, DxItem, DxGroupItem, DxLabel, DxTabbedItem, DxTab, DxSimpleItem, DxPatternRule } from 'devextreme-vue/form';
import DxTextArea from 'devextreme-vue/text-area';
import type { EntityConfig, FieldConfig } from './types';
import { formatApiError } from '../api/errors';
import LookupBox from './LookupBox.vue';

/** Form.Item option names that collide with entity dataField → use explicit editors. */
const RESERVED_ITEM_OPTION_NAMES = new Set([
  'template',
  'items',
  'tabs',
  'label',
  'editorOptions',
  'editorType',
  'validationRules',
  'buttonOptions',
]);

function useExplicitEditor(f: FieldConfig): boolean {
  return f.editorType === 'dxTextArea' || RESERVED_ITEM_OPTION_NAMES.has(f.dataField);
}

const props = defineProps({
  visible: { type: Boolean, required: true },
  mode: { type: String as PropType<'create' | 'edit'>, required: true },
  config: { type: Object as PropType<EntityConfig>, required: true },
  initialValue: { type: Object as PropType<any>, default: () => ({}) },
});

const emit = defineEmits<{
  (e: 'update:visible', v: boolean): void;
  (e: 'save', payload: any): void;
}>();

const formData = ref<any>({ ...(props.initialValue ?? {}) });
const saving = ref(false);
const errorText = ref<string | null>(null);
const formRef = ref<InstanceType<typeof DxForm> | null>(null);

watch(
  () => props.initialValue,
  (v) => { formData.value = { ...(v ?? {}) }; errorText.value = null; },
  { immediate: true },
);

const keyExpr = computed(() => props.config.keyExpr ?? 'id');

const visibleFields = computed<FieldConfig[]>(() =>
  props.config.fields.filter((f) => {
    if (f.hidden) return false;
    if (props.mode === 'create' && f.editOnly) return false;
    if (props.mode === 'edit' && f.createOnly) return false;
    if (props.mode === 'edit' && f.editOnly && f.dataField === keyExpr.value) return false;
    return true;
  }),
);

const tabsList = computed(() => {
  if (props.config.tabs && props.config.tabs.length) return props.config.tabs;
  const set = new Set<string>();
  for (const f of visibleFields.value) if (f.tab) set.add(f.tab);
  return Array.from(set);
});

const hasTabs = computed(() => tabsList.value.length > 0);

const formColCount = computed(() => props.config.formColCount ?? 2);

const fieldsByTab = computed(() => {
  const groups = new Map<string, FieldConfig[]>();
  for (const t of tabsList.value) groups.set(t, []);
  const orphan: FieldConfig[] = [];
  for (const f of visibleFields.value) {
    if (f.tab) groups.get(f.tab)?.push(f);
    else orphan.push(f);
  }
  return { groups, orphan };
});

const title = computed(() => {
  if (props.mode === 'create') return `Создать: ${props.config.title}`;
  const id = formData.value?.[keyExpr.value];
  const idPart = id != null && id !== '' ? ` #${id}` : '';
  return `Редактировать: ${props.config.title}${idPart}`;
});

function fieldItemName(dataField: string): string {
  return `eef_${dataField}`;
}

function close() { emit('update:visible', false); }

function setFieldValue(dataField: string, value: unknown) {
  const next = { ...formData.value, [dataField]: value };
  const field = visibleFields.value.find((f) => f.dataField === dataField);
  if (field?.lookup && field.mapLookupItem) {
    const item =
      value == null || value === ''
        ? null
        : field.lookup.byId.value.get(value as string | number) ?? null;
    Object.assign(next, field.mapLookupItem(item));
  }
  for (const f of visibleFields.value) {
    if (!f.clearWhen?.includes(dataField)) continue;
    next[f.dataField] = f.lookupMultiple ? [] : null;
  }
  formData.value = next;
}

function lookupItemsFilter(f: FieldConfig): ((item: any) => boolean) | null {
  if (!f.lookupFilter) return null;
  return (item: any) => f.lookupFilter!(item, formData.value);
}

function lookupReloadKey(f: FieldConfig): string {
  if (!f.clearWhen?.length && !f.lookupFilter) return '';
  const keys = f.clearWhen?.length
    ? f.clearWhen
    : Object.keys(formData.value);
  return keys.map((k) => String(formData.value[k] ?? '')).join('|');
}

function fieldEditorOptions(f: FieldConfig): Record<string, unknown> | undefined {
  if (!f.digitsOnly) return f.editorOptions;
  return {
    ...f.editorOptions,
    inputAttr: {
      ...((f.editorOptions?.inputAttr as object | undefined) ?? {}),
      inputmode: 'numeric',
      pattern: '[0-9]*',
    },
    onKeyDown(e: { event?: KeyboardEvent }) {
      const ev = e.event;
      if (!ev || ev.ctrlKey || ev.metaKey || ev.altKey) return;
      if (ev.key.length === 1 && !/\d/.test(ev.key)) ev.preventDefault();
    },
  };
}

function validateRequiredFields(): string | null {
  for (const f of visibleFields.value) {
    if (!f.required) continue;
    const value = formData.value[f.dataField];
    if (value == null || value === '') {
      return `Заполните поле «${f.label ?? f.dataField}»`;
    }
  }
  return null;
}

function validateDigitsOnlyFields(): string | null {
  for (const f of visibleFields.value) {
    if (!f.digitsOnly) continue;
    const value = formData.value[f.dataField];
    if (value == null || value === '') continue;
    if (!/^\d+$/.test(String(value))) {
      return `Поле «${f.label ?? f.dataField}» может содержать только цифры`;
    }
  }
  return null;
}

async function save() {
  errorText.value = null;
  const requiredError = validateRequiredFields();
  if (requiredError) {
    errorText.value = requiredError;
    return;
  }
  const digitsError = validateDigitsOnlyFields();
  if (digitsError) {
    errorText.value = digitsError;
    return;
  }
  const form = formRef.value?.instance;
  if (form) {
    const validation = form.validate();
    if (!validation.isValid) return;
  }
  saving.value = true;
  try {
    emit('save', JSON.parse(JSON.stringify(formData.value)));
  } catch (e) {
    errorText.value = formatApiError(e);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <DxPopup
    :visible="visible"
    :title="title"
    :width="config.popupWidth ?? 720"
    :height="config.popupHeight ?? 'auto'"
    :drag-enabled="true"
    :show-close-button="true"
    :close-on-outside-click="false"
    @hidden="close"
  >
    <div class="eep-body">
      <DxForm
        ref="formRef"
        :form-data="formData"
        :col-count="1"
        :show-colon-after-label="true"
        label-location="top"
      >
        <template v-if="!hasTabs">
          <template v-for="f in fieldsByTab.orphan" :key="f.dataField">
            <DxSimpleItem v-if="f.lookup" :name="fieldItemName(f.dataField)" :col-span="f.colSpan ?? 1">
              <DxLabel :text="f.label ?? f.dataField" />
              <LookupBox
                :model-value="formData[f.dataField]"
                :lookup="f.lookup"
                :multiple="Boolean(f.lookupMultiple)"
                :items-filter="lookupItemsFilter(f)"
                :reload-key="lookupReloadKey(f)"
                :placeholder="f.label ?? f.dataField"
                :disabled="Boolean(f.editorOptions?.disabled || f.editorOptions?.readOnly)"
                @update:model-value="(v) => setFieldValue(f.dataField, v)"
              />
            </DxSimpleItem>
            <DxSimpleItem
              v-else-if="useExplicitEditor(f)"
              :name="fieldItemName(f.dataField)"
              :col-span="f.colSpan ?? 1"
              :is-required="f.required"
              :help-text="f.helpText"
            >
              <DxLabel :text="f.label ?? f.dataField" />
              <DxTextArea
                :value="formData[f.dataField] ?? ''"
                v-bind="f.editorOptions ?? {}"
                @value-changed="(e: any) => setFieldValue(f.dataField, e.value)"
              />
            </DxSimpleItem>
            <DxItem
              v-else
              :name="fieldItemName(f.dataField)"
              :data-field="f.dataField"
              :editor-type="f.editorType ?? 'dxTextBox'"
              :editor-options="fieldEditorOptions(f)"
              :col-span="f.colSpan"
              :is-required="f.required"
              :help-text="f.helpText"
            >
              <DxLabel :text="f.label ?? f.dataField" />
              <DxPatternRule
                v-if="f.digitsOnly"
                :pattern="/^\d+$/"
                message="Допустимы только цифры"
              />
            </DxItem>
          </template>
        </template>

        <template v-else>
          <DxTabbedItem>
            <DxTab
              v-for="tab in tabsList"
              :key="tab"
              :title="tab"
              :col-count="formColCount"
            >
              <template v-for="f in (fieldsByTab.groups.get(tab) || [])" :key="f.dataField">
                <DxSimpleItem v-if="f.lookup" :name="fieldItemName(f.dataField)" :col-span="f.colSpan ?? 1">
                  <DxLabel :text="f.label ?? f.dataField" />
                  <LookupBox
                    :model-value="formData[f.dataField]"
                    :lookup="f.lookup"
                    :multiple="Boolean(f.lookupMultiple)"
                    :items-filter="lookupItemsFilter(f)"
                    :reload-key="lookupReloadKey(f)"
                    :placeholder="f.label ?? f.dataField"
                    :disabled="Boolean(f.editorOptions?.disabled || f.editorOptions?.readOnly)"
                    @update:model-value="(v) => setFieldValue(f.dataField, v)"
                  />
                </DxSimpleItem>
                <DxSimpleItem
                  v-else-if="useExplicitEditor(f)"
                  :name="fieldItemName(f.dataField)"
                  :col-span="f.colSpan ?? 1"
                  :is-required="f.required"
                  :help-text="f.helpText"
                >
                  <DxLabel :text="f.label ?? f.dataField" />
                  <DxTextArea
                    :value="formData[f.dataField] ?? ''"
                    v-bind="f.editorOptions ?? {}"
                    @value-changed="(e: any) => setFieldValue(f.dataField, e.value)"
                  />
                </DxSimpleItem>
                <DxItem
                  v-else
                  :name="fieldItemName(f.dataField)"
                  :data-field="f.dataField"
                  :editor-type="f.editorType ?? 'dxTextBox'"
                  :editor-options="fieldEditorOptions(f)"
                  :col-span="f.colSpan"
                  :is-required="f.required"
                  :help-text="f.helpText"
                >
                  <DxLabel :text="f.label ?? f.dataField" />
                  <DxPatternRule
                    v-if="f.digitsOnly"
                    :pattern="/^\d+$/"
                    message="Допустимы только цифры"
                  />
                </DxItem>
              </template>
            </DxTab>
          </DxTabbedItem>

          <DxGroupItem v-if="fieldsByTab.orphan.length" :col-count="formColCount">
            <template v-for="f in fieldsByTab.orphan" :key="f.dataField">
              <DxSimpleItem v-if="f.lookup" :name="fieldItemName(f.dataField)" :col-span="f.colSpan ?? 1">
                <DxLabel :text="f.label ?? f.dataField" />
                <LookupBox
                  :model-value="formData[f.dataField]"
                  :lookup="f.lookup"
                  :multiple="Boolean(f.lookupMultiple)"
                  :items-filter="lookupItemsFilter(f)"
                  :reload-key="lookupReloadKey(f)"
                  :placeholder="f.label ?? f.dataField"
                  :disabled="Boolean(f.editorOptions?.disabled || f.editorOptions?.readOnly)"
                  @update:model-value="(v) => setFieldValue(f.dataField, v)"
                />
              </DxSimpleItem>
              <DxSimpleItem
                v-else-if="useExplicitEditor(f)"
                :name="fieldItemName(f.dataField)"
                :col-span="f.colSpan ?? 1"
                :is-required="f.required"
                :help-text="f.helpText"
              >
                <DxLabel :text="f.label ?? f.dataField" />
                <DxTextArea
                  :value="formData[f.dataField] ?? ''"
                  v-bind="f.editorOptions ?? {}"
                  @value-changed="(e: any) => setFieldValue(f.dataField, e.value)"
                />
              </DxSimpleItem>
              <DxItem
                v-else
                :name="fieldItemName(f.dataField)"
                :data-field="f.dataField"
                :editor-type="f.editorType ?? 'dxTextBox'"
                :editor-options="fieldEditorOptions(f)"
                :col-span="f.colSpan"
                :is-required="f.required"
                :help-text="f.helpText"
              >
                <DxLabel :text="f.label ?? f.dataField" />
                <DxPatternRule
                  v-if="f.digitsOnly"
                  :pattern="/^\d+$/"
                  message="Допустимы только цифры"
                />
              </DxItem>
            </template>
          </DxGroupItem>
        </template>
      </DxForm>

      <slot name="extra" :form-data="formData" :mode="mode" />

      <div v-if="errorText" class="eep-error">{{ errorText }}</div>
    </div>

    <DxToolbarItem
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="{ text: 'Сохранить', type: 'default', stylingMode: 'contained', disabled: saving, onClick: save }"
    />
    <DxToolbarItem
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="{ text: 'Отмена', stylingMode: 'outlined', onClick: close }"
    />
  </DxPopup>
</template>

<style scoped>
.eep-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
  max-height: min(72vh, calc(100vh - 200px));
  overflow-x: hidden;
  overflow-y: auto;
}

/* Avoid a second scrollbar from DxForm TabPanel nesting. */
.eep-body :deep(.dx-tabpanel),
.eep-body :deep(.dx-tabpanel-container),
.eep-body :deep(.dx-multiview-wrapper),
.eep-body :deep(.dx-multiview-item),
.eep-body :deep(.dx-multiview-item-content) {
  height: auto !important;
  overflow: visible !important;
}

.eep-body :deep(.dx-field-item) {
  padding-top: 2px;
  padding-bottom: 2px;
}

.eep-error {
  color: #c0392b;
  padding: 8px;
  background: #fdecea;
  border-radius: 4px;
  font-size: 13px;
  white-space: pre-wrap;
}
</style>
